import { useState } from 'react'
import { motion } from 'framer-motion'
import { KeyRound } from 'lucide-react'
import type { StepSecret as StepSecretType, StorySecret } from '@/types/storytelling'

interface StepSecretProps {
  step: StepSecretType
  secret: StorySecret
  onUnlock: () => void
}

export function StepSecret({ step, secret, onUnlock }: StepSecretProps) {
  const [unlocked, setUnlocked] = useState(false)

  const handleUnlock = () => {
    setUnlocked(true)
    onUnlock()
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-10 text-center" style={{ background: 'linear-gradient(180deg, #2D1B4E 0%, #4A2D7A 50%, var(--color-story-primary) 100%)' }}>
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }} className="w-20 h-20 rounded-full flex items-center justify-center mb-5" style={{ background: 'radial-gradient(circle, var(--color-story-accent) 0%, rgba(232,168,56,0.3) 60%, transparent 70%)', boxShadow: '0 0 40px rgba(232,168,56,0.3)' }}>
        <KeyRound className="w-9 h-9 text-amber-900" />
      </motion.div>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--color-story-accent)' }}>Story Secret Unlocked</motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-2xl font-bold text-white mb-3">{secret.title}</motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="text-sm text-white/85 leading-relaxed max-w-xs mb-2">{step.revealLine}</motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }} className="text-sm text-white/75 leading-relaxed max-w-xs mb-5">{step.principle}</motion.p>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="rounded-xl p-4 mb-6 max-w-xs" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
        <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-story-accent)' }}>How writers use this:</p>
        <p className="text-sm text-white/80 leading-relaxed">{step.writerBridge}</p>
      </motion.div>
      <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} onClick={handleUnlock} disabled={unlocked} className="px-7 py-3 rounded-xl text-sm font-bold transition-transform hover:scale-105 disabled:opacity-60 disabled:cursor-default" style={{ background: unlocked ? 'rgba(255,255,255,0.2)' : 'linear-gradient(135deg, var(--color-story-accent), #D49A2E)', color: unlocked ? 'white' : '#2D1B4E' }}>
        {unlocked ? 'Added to My Story Secrets ✓' : 'Add to My Story Secrets'}
      </motion.button>
    </div>
  )
}
